import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { catchError, from, map, mergeMap, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ToastService } from '../../core/services/toast/toast.service';

export interface ReportPayloadDto {
    coachEmail: string | null;
    begin: Date | null;
    end: Date | null;
}

/**
 *  Service for generating and downloading reports.
 *  This service handles the requests to the backend for generating reports and downloading them as files.
 *  It also manages the display of success or error messages during the download process.
 */
@Injectable({
    providedIn: 'root'
})
export class ReportService {
    constructor(
        private http: HttpClient,
        private toastService: ToastService,
    ) { }

    private downloadFile = (reportName: string, blobResponse: HttpResponse<Blob>) => {
        const contentDisposition = blobResponse.headers.get('content-disposition');
        const match = contentDisposition?.match(/attachment;\s*filename=([^;]+)/i);
        const fileName = match?.[1]
            ? match[1].replace(/"/g, '')
            : reportName + '.docx';

        const reportBlob = new Blob([blobResponse.body!], {
            type: blobResponse.body?.type || 'application/octet-stream',
        });
        // FileSaver delays URL cleanup so the browser can consume the Blob.
        saveAs(reportBlob, fileName);

        this.toastService.success('Report generated. Download started.');
    }

    private async readErrorMessage(error: HttpErrorResponse): Promise<string> {
        if (error.error instanceof Blob) {
            const message = (await error.error.text()).trim();
            if (message) {
                return message;
            }
        } else if (typeof error.error === 'string' && error.error.trim()) {
            return error.error.trim();
        }

        return error.message || 'Report download failed.';
    }

    generateReport(reportName: string, payload: ReportPayloadDto) {
        this.toastService.custom({
            severity: 'info',
            summary: 'Downloading report...',
        });
        
        return this.http.post(
            environment.apiBaseURL + `Reports/${reportName}`,
            payload,
            { observe: 'response', responseType: 'blob' })
            .pipe(
                map((blobResponse: HttpResponse<Blob>) => {
                    this.downloadFile(reportName, blobResponse);
                }),
                catchError((error: HttpErrorResponse) =>
                    from(this.readErrorMessage(error)).pipe(
                        mergeMap((message) => {
                            this.toastService.error(message);
                            return throwError(() => error);
                        })
                    )
                )
            );
    }
}
